/* ══════════════════════════════════════════════════════════════════════════
 * ordersApi — customer order self-edit / self-cancel API client
 *
 * Talks to the Laravel backend's /api/orders endpoints. Authentication is a
 * stateless edit token issued at login (email + order number) and also embedded
 * in the edit link emailed with the order confirmation.
 *
 * `brandSlug` here is the BACKEND brand slug (e.g. 'clsdk', 'abcse'), not the
 * frontend route slug ('cls-dk', 'abc-se').
 * ══════════════════════════════════════════════════════════════════════════ */

import { CMS_API_URL } from '../../../../config/apiBase';

async function request(path, { method = 'GET', token, body, adminToken } = {}) {
  const headers = { Accept: 'application/json' };
  if (body) headers['Content-Type'] = 'application/json';
  if (token) headers['X-Order-Token'] = token;
  // Admin-edit token: lets an authenticated admin edit an order that is no
  // longer customer-editable (the backend keeps the paid-card price-lock).
  if (adminToken) headers['X-Admin-Edit'] = adminToken;

  const res = await fetch(`${CMS_API_URL}/api/orders/${path}`, {
    method,
    headers,
    ...(body ? { body: JSON.stringify(body) } : {}),
  });

  let data = null;
  try {
    data = await res.json();
  } catch {
    data = null;
  }

  if (!res.ok) {
    const message = (data && (data.error || data.message)) || 'Something went wrong. Please try again.';
    const err = new Error(message);
    err.status = res.status;
    err.code = data && data.code;
    throw err;
  }
  return data;
}

/** Log in with email + order number. Returns { token, order }. */
export function loginToOrder(brandSlug, { email, orderNumber }) {
  return request(`${brandSlug}/login`, {
    method: 'POST',
    body: { email, order_number: orderNumber },
  });
}

/** Fetch an order for editing using an edit token. Returns { order }. */
export function fetchOrder(brandSlug, orderNumber, token, adminToken) {
  return request(`${brandSlug}/${encodeURIComponent(orderNumber)}?token=${encodeURIComponent(token)}`, {
    token,
    adminToken,
  });
}

/**
 * Edit a single order line. Pass `{ remove: true }` to delete it, otherwise
 * `{ quantity }` and/or `{ options }`. Returns { order }.
 */
export function patchOrderItem(brandSlug, orderNumber, token, payload, adminToken) {
  return request(`${brandSlug}/${encodeURIComponent(orderNumber)}/items`, {
    method: 'PATCH',
    token,
    adminToken,
    body: payload,
  });
}

/**
 * Cancel the order (auto-refunds where supported). Returns { cancelled, refunded, warning }.
 * `adminToken` only attributes the cancellation in the backend's order edit log (Admin
 * vs Customer) — it grants nothing: the backend applies the customer rules to a cancel.
 */
export function cancelOrder(brandSlug, orderNumber, token, adminToken) {
  return request(`${brandSlug}/${encodeURIComponent(orderNumber)}/cancel`, {
    method: 'POST',
    token,
    adminToken,
  });
}
